import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Bold,
  Check,
  Image as ImageIcon,
  Italic,
  Link2,
  List,
  ListOrdered,
  Loader2,
  Maximize2,
  Minimize2,
  Paperclip,
  Pencil,
  Redo2,
  Trash2,
  Undo2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ClickableLinkPlugin } from '@lexical/react/LexicalClickableLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { mergeRegister } from '@lexical/utils';
import { $isListNode, INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListItemNode, ListNode, REMOVE_LIST_COMMAND } from '@lexical/list';
import { $isLinkNode, LinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import {
  $findMatchingParent,
  $getNearestNodeFromDOMNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  $createParagraphNode,
  $insertNodes,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  DecoratorNode,
  FORMAT_TEXT_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  REDO_COMMAND,
  UNDO_COMMAND,
} from 'lexical';
import { useAuthenticatedFetch } from '../../hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import i18n from '@/i18n/config';
import { useDiagramDocument } from '../../contexts/DiagramDocumentContext';
import { projectRichTextToPlainText } from '../../lib/richText/notesProjection';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin';
import { NOTES_MARKDOWN_TRANSFORMERS, normalizeNotesToLexicalJSON } from '../../lib/richText/lexicalNotes';

type ShapeNotesEditorProps = {
  nodeId: string;
  value: unknown;
  onChange: (value: Record<string, unknown>) => void;
  disabled?: boolean;
};

type AssetRecord = {
  id: string;
  diagramId: string;
  shapeId?: string | null;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  assetType: 'image' | 'file';
};

type AssetUploadResponse = {
  asset: AssetRecord;
  uploadUrl: string;
  method: 'PUT';
  headers?: Record<string, string>;
};

type AssetDownloadResponse = {
  asset: AssetRecord;
  downloadUrl: string;
  expiresIn: number;
};

const BUTTON_CLASS =
  'flex h-7 min-w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40';
const ACTIVE_BUTTON_CLASS = 'bg-muted text-foreground';

type LinkEditorState = {
  url: string;
  originalUrl: string;
  nodeKey: string | null;
  isEditing: boolean;
  position: {
    top: number;
    left: number;
  };
};

function normalizeLexicalValue(value: unknown): { serialized: string; parsed: Record<string, unknown> } {
  const serialized = normalizeNotesToLexicalJSON(value);
  const parsed = JSON.parse(serialized) as Record<string, unknown>;
  return { serialized: JSON.stringify(parsed), parsed };
}

async function uploadAsset(
  authenticatedFetch: ReturnType<typeof useAuthenticatedFetch>,
  diagramId: string,
  shapeId: string,
  file: File,
): Promise<AssetRecord> {
  const response = await authenticatedFetch('/api/assets/presign', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      diagramId,
      shapeId,
      fileName: file.name,
      mimeType: file.type || 'application/octet-stream',
      sizeBytes: file.size,
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({
      error: i18n.t('diagram:shapeNotes.uploadErrors.prepareFailed', {
        defaultValue: 'Failed to prepare asset upload',
      }),
    })) as { error?: string };
    throw new Error(
      errorBody.error ||
        (i18n.t('diagram:shapeNotes.uploadErrors.prepareFailed', {
          defaultValue: 'Failed to prepare asset upload',
        }) as string),
    );
  }

  const payload = await response.json() as AssetUploadResponse;
  const uploadResponse = await fetch(payload.uploadUrl, {
    method: payload.method,
    headers: payload.headers,
    body: file,
  });

  if (!uploadResponse.ok) {
    throw new Error(
      i18n.t('diagram:shapeNotes.uploadErrors.uploadFailed', {
        fileName: file.name,
        defaultValue: 'Failed to upload {{fileName}}',
      }) as string,
    );
  }

  return payload.asset;
}

function formatFileSize(sizeBytes: number): string {
  if (sizeBytes < 1024) return `${sizeBytes} B`;
  if (sizeBytes < 1024 * 1024) return `${(sizeBytes / 1024).toFixed(1)} KB`;
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uploadErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

const AssetImageComponent: React.FC<{ assetId: string; fileName: string }> = ({ assetId, fileName }) => {
  const { t } = useTranslation('diagram');
  const authenticatedFetch = useAuthenticatedFetch();
  const [downloadUrl, setDownloadUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    async function loadAsset() {
      try {
        const response = await authenticatedFetch(`/api/assets/${assetId}`);
        if (!response.ok) {
          throw new Error('Failed to load image asset');
        }
        const payload = await response.json() as AssetDownloadResponse;
        if (!cancelled) {
          setDownloadUrl(payload.downloadUrl);
        }
      } catch {
        if (!cancelled) {
          setDownloadUrl(null);
        }
      }
    }

    loadAsset();
    return () => {
      cancelled = true;
    };
  }, [assetId, authenticatedFetch]);

  return (
    <div className="overflow-hidden rounded-xl border bg-muted/20">
      {downloadUrl ? (
        <img src={downloadUrl} alt={fileName} className="max-h-72 w-full object-cover" />
      ) : (
        <div className="flex min-h-28 flex-col items-center justify-center gap-2 px-4 py-8 text-sm text-muted-foreground">
          <div className="flex max-w-full items-center justify-center gap-2">
            <ImageIcon className="h-4 w-4 shrink-0" />
            <span className="truncate">{fileName}</span>
          </div>
          <span className="text-xs text-muted-foreground/80">
            {t('shapeNotes.assets.loadingImagePreview', { defaultValue: 'Loading preview…' })}
          </span>
        </div>
      )}
    </div>
  );
};

const AssetAttachmentComponent: React.FC<{
  assetId: string;
  fileName: string;
  mimeType: string;
  sizeBytes?: number;
}> = ({ assetId, fileName, mimeType, sizeBytes }) => {
  const { t } = useTranslation('diagram');
  const authenticatedFetch = useAuthenticatedFetch();
  const [downloadUrl, setDownloadUrl] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    async function loadAsset() {
      try {
        const response = await authenticatedFetch(`/api/assets/${assetId}`);
        if (!response.ok) {
          throw new Error('Failed to load attachment asset');
        }
        const payload = await response.json() as AssetDownloadResponse;
        if (!cancelled) {
          setDownloadUrl(payload.downloadUrl);
        }
      } catch {
        if (!cancelled) {
          setDownloadUrl(null);
        }
      }
    }

    loadAsset();
    return () => {
      cancelled = true;
    };
  }, [assetId, authenticatedFetch]);

  return (
    <div className="flex items-center gap-3 rounded-xl border bg-background px-3 py-2">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <Paperclip className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium">{fileName}</div>
        <div className="truncate text-xs text-muted-foreground">
          {mimeType}
          {sizeBytes ? ` • ${formatFileSize(sizeBytes)}` : ''}
        </div>
      </div>
      {downloadUrl ? (
        <a href={downloadUrl} target="_blank" rel="noreferrer" className="text-xs font-medium text-primary hover:underline">
          {t('shapeNotes.assets.openAttachment', { defaultValue: 'Open' })}
        </a>
      ) : (
        <span className="text-xs text-muted-foreground">
          {t('shapeNotes.assets.previewUnavailable', { defaultValue: 'Unavailable' })}
        </span>
      )}
    </div>
  );
};

const AssetNodeShell: React.FC<{
  nodeKey: string;
  children: React.ReactNode;
}> = ({ nodeKey, children }) => {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const rootRef = useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CLICK_COMMAND,
        (event) => {
          const target = event.target as Node | null;
          const rootElement = rootRef.current;
          if (!target || !rootElement || !rootElement.contains(target)) {
            return false;
          }

          if (target instanceof HTMLElement && target.closest('a')) {
            return false;
          }

          event.preventDefault();
          if (!event.shiftKey) {
            clearSelection();
          }
          setSelected(!isSelected);
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        (event) => {
          const selection = $getSelection();
          if (!$isNodeSelection(selection) || !isSelected) {
            return false;
          }

          event.preventDefault();
          editor.update(() => {
            const node = $getNodeByKey(nodeKey);
            node?.remove();
          });
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        (event) => {
          const selection = $getSelection();
          if (!$isNodeSelection(selection) || !isSelected) {
            return false;
          }

          event.preventDefault();
          editor.update(() => {
            const node = $getNodeByKey(nodeKey);
            node?.remove();
          });
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    );
  }, [clearSelection, editor, isSelected, nodeKey, setSelected]);

  return (
    <div
      ref={rootRef}
      className="relative my-2 rounded-2xl"
      data-testid="tid.tools.shapeNotes.assetNode"
      data-selected={isSelected ? 'true' : 'false'}
    >
      {isSelected ? (
        <>
          <div className="pointer-events-none absolute -left-2 top-1/2 h-8 w-1 -translate-y-1/2 rounded-full bg-primary" />
        </>
      ) : null}
      {children}
    </div>
  );
};

class AssetImageNode extends DecoratorNode<React.ReactNode> {
  __assetId: string;
  __fileName: string;

  static getType(): string {
    return 'asset-image';
  }

  static clone(node: AssetImageNode): AssetImageNode {
    return new AssetImageNode(node.__assetId, node.__fileName, node.__key);
  }

  static importJSON(serializedNode: any): AssetImageNode {
    return new AssetImageNode(serializedNode.assetId, serializedNode.fileName);
  }

  constructor(assetId: string, fileName: string, key?: string) {
    super(key);
    this.__assetId = assetId;
    this.__fileName = fileName;
  }

  createDOM(): HTMLElement {
    return document.createElement('div');
  }

  updateDOM(): false {
    return false;
  }

  decorate(): React.ReactNode {
    return (
      <AssetNodeShell nodeKey={this.__key}>
        <AssetImageComponent assetId={this.__assetId} fileName={this.__fileName} />
      </AssetNodeShell>
    );
  }

  exportJSON(): any {
    return {
      type: 'asset-image',
      version: 1,
      assetId: this.__assetId,
      fileName: this.__fileName,
    };
  }
}

class AssetAttachmentNode extends DecoratorNode<React.ReactNode> {
  __assetId: string;
  __fileName: string;
  __mimeType: string;
  __sizeBytes?: number;

  static getType(): string {
    return 'asset-attachment';
  }

  static clone(node: AssetAttachmentNode): AssetAttachmentNode {
    return new AssetAttachmentNode(node.__assetId, node.__fileName, node.__mimeType, node.__sizeBytes, node.__key);
  }

  static importJSON(serializedNode: any): AssetAttachmentNode {
    return new AssetAttachmentNode(
      serializedNode.assetId,
      serializedNode.fileName,
      serializedNode.mimeType,
      serializedNode.sizeBytes,
    );
  }

  constructor(assetId: string, fileName: string, mimeType: string, sizeBytes?: number, key?: string) {
    super(key);
    this.__assetId = assetId;
    this.__fileName = fileName;
    this.__mimeType = mimeType;
    this.__sizeBytes = sizeBytes;
  }

  createDOM(): HTMLElement {
    return document.createElement('div');
  }

  updateDOM(): false {
    return false;
  }

  decorate(): React.ReactNode {
    return (
      <AssetNodeShell nodeKey={this.__key}>
        <AssetAttachmentComponent
          assetId={this.__assetId}
          fileName={this.__fileName}
          mimeType={this.__mimeType}
          sizeBytes={this.__sizeBytes}
        />
      </AssetNodeShell>
    );
  }

  exportJSON(): any {
    return {
      type: 'asset-attachment',
      version: 1,
      assetId: this.__assetId,
      fileName: this.__fileName,
      mimeType: this.__mimeType,
      sizeBytes: this.__sizeBytes,
    };
  }
}

function $createAssetImageNode(assetId: string, fileName: string): AssetImageNode {
  return new AssetImageNode(assetId, fileName);
}

function $createAssetAttachmentNode(
  assetId: string,
  fileName: string,
  mimeType: string,
  sizeBytes?: number,
): AssetAttachmentNode {
  return new AssetAttachmentNode(assetId, fileName, mimeType, sizeBytes);
}

function insertAssetNodes(nodes: [DecoratorNode<React.ReactNode>, ReturnType<typeof $createParagraphNode>]): void {
  const selection = $getSelection();
  if (selection) {
    $insertNodes(nodes);
    return;
  }

  const root = $getRoot();
  root.append(...nodes);
}

function getToolbarButtonClass(isActive = false): string {
  return isActive ? `${BUTTON_CLASS} ${ACTIVE_BUTTON_CLASS}` : BUTTON_CLASS;
}

function getSelectedLinkNode(): LinkNode | null {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return null;
  }

  const nodes = selection.getNodes();
  for (const node of nodes) {
    const direct = $isLinkNode(node) ? node : null;
    if (direct) {
      return direct;
    }

    const parentLink = $findMatchingParent(node, (parentNode) => $isLinkNode(parentNode));
    if ($isLinkNode(parentLink)) {
      return parentLink;
    }
  }

  const anchorNode = selection.anchor.getNode();
  const direct = $isLinkNode(anchorNode) ? anchorNode : null;
  if (direct) {
    return direct;
  }

  const parentLink = $findMatchingParent(anchorNode, (parentNode) => $isLinkNode(parentNode));
  return $isLinkNode(parentLink) ? parentLink : null;
}

function normalizeSafeHttpUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

function getLinkPanelPosition(
  rect: DOMRect | null | undefined,
  containerRect: DOMRect | null | undefined,
  boundsRect: DOMRect | null | undefined,
): { top: number; left: number } {
  const fallback = { top: 52, left: 8 };
  if (!rect || !containerRect) {
    return fallback;
  }

  const panelWidth = 400;
  const panelHeight = 84;
  const gutter = 8;
  const minTop = containerRect.height + gutter;
  const preferredBelowTop = rect.bottom - containerRect.top + gutter;
  const preferredAboveTop = rect.top - containerRect.top - panelHeight - gutter;
  const unclampedLeft = rect.left - containerRect.left;
  const maxLeft = Math.max(gutter, containerRect.width - panelWidth - gutter);
  const maxTop = boundsRect
    ? Math.max(minTop, boundsRect.bottom - containerRect.top - panelHeight - gutter)
    : Number.POSITIVE_INFINITY;

  let top = preferredBelowTop;
  if (preferredBelowTop > maxTop) {
    top = Math.max(minTop, preferredAboveTop);
  }

  top = Math.min(top, maxTop);

  return {
    top,
    left: Math.min(Math.max(gutter, unclampedLeft), maxLeft),
  };
}

function unwrapLinkNode(node: LinkNode): void {
  const children = node.getChildren();
  children.forEach((child) => node.insertBefore(child));
  node.remove();
}

const ToolbarTooltipButton: React.FC<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    label: string;
    children: React.ReactNode;
  }
> = ({ label, children, className, disabled, ...props }) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span className={disabled ? 'inline-flex cursor-not-allowed' : ''}>
        <button
          type="button"
          className={className}
          disabled={disabled}
          aria-label={label}
          title={label}
          {...props}
        >
          {children}
        </button>
      </span>
    </TooltipTrigger>
    <TooltipContent side="top" sideOffset={6}>
      {label}
    </TooltipContent>
  </Tooltip>
);

function getListTypeFromNode(node: { getParent?: () => unknown } | null | undefined): 'bullet' | 'number' | null {
  let current: any = node;

  while (current) {
    if ($isListNode(current)) {
      const listType = current.getListType?.();
      if (listType === 'bullet' || listType === 'number') {
        return listType;
      }
      return null;
    }
    current = current.getParent?.() ?? null;
  }

  return null;
}

const Placeholder: React.FC = () => {
  const { t } = useTranslation('diagram');
  return (
    <div className="pointer-events-none absolute top-3 left-4 text-sm text-muted-foreground/70">
      {t('shapeNotes.placeholder', {
        defaultValue: 'Add context: type notes, upload images, or link files',
      })}
    </div>
  );
};

const FloatingLinkEditorPanel: React.FC<{
  state: LinkEditorState;
  onUrlChange: (nextUrl: string) => void;
  onApply: () => void;
  onRemove: () => void;
  onStartEditing: () => void;
  onCancelEditing: () => void;
}> = ({ state, onUrlChange, onApply, onRemove, onStartEditing, onCancelEditing }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!state.isEditing) {
      return;
    }
    inputRef.current?.focus();
    try {
      inputRef.current?.select();
    } catch {}
  }, [state.isEditing]);

  return (
    <div
      className="absolute z-30 w-[min(400px,calc(100%-16px))]"
      style={{ top: state.position.top, left: state.position.left }}
      data-testid="tid.tools.shapeNotes.linkEditor"
    >
      <div className="rounded-xl border border-border bg-background px-4 py-3 shadow-[0_12px_30px_rgba(15,23,42,0.14)]">
        {state.isEditing ? (
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={state.url}
              onChange={(event) => onUrlChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  onApply();
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  onCancelEditing();
                }
              }}
              placeholder="https://"
              className="h-9 w-full min-w-0 flex-1 rounded-full border-0 bg-muted px-4 text-sm outline-none placeholder:text-muted-foreground focus:outline-none focus-visible:outline-none focus-visible:ring-0"
              data-testid="tid.tools.shapeNotes.linkEditor.input"
            />
            <button
              type="button"
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-background transition-opacity hover:opacity-80"
              onClick={onCancelEditing}
              aria-label="Cancel link editing"
              data-testid="tid.tools.shapeNotes.linkEditor.cancel"
            >
              <X className="h-3 w-3" />
            </button>
            <button
              type="button"
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-background transition-opacity hover:opacity-80"
              onClick={onApply}
              aria-label="Apply link"
              data-testid="tid.tools.shapeNotes.linkEditor.apply"
            >
              <Check className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <a
              href={state.url.trim()}
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-0 flex-1 truncate text-base text-primary underline underline-offset-2"
              data-testid="tid.tools.shapeNotes.linkEditor.open"
            >
              {state.url.trim()}
            </a>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted"
                onClick={onStartEditing}
                aria-label="Edit link"
                data-testid="tid.tools.shapeNotes.linkEditor.edit"
              >
                <Pencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted"
                onClick={onRemove}
                aria-label="Remove link"
                data-testid="tid.tools.shapeNotes.linkEditor.remove"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ToolbarPlugin: React.FC<{
  disabled: boolean;
  onUploadImage: () => void;
  onUploadFile: () => void;
  isUploading: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}> = ({ disabled, onUploadImage, onUploadFile, isUploading, isExpanded, onToggleExpanded }) => {
  const { t } = useTranslation('diagram');
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [activeListType, setActiveListType] = useState<'bullet' | 'number' | null>(null);
  const [linkEditor, setLinkEditor] = useState<LinkEditorState | null>(null);
  const linkEditorRef = useRef<HTMLDivElement | null>(null);
  const toolbarContainerRef = useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    return editor.registerCommand(
      CAN_UNDO_COMMAND,
      (payload) => {
        setCanUndo(payload);
        return false;
      },
      1,
    );
  }, [editor]);

  React.useEffect(() => {
    return editor.registerCommand(
      CAN_REDO_COMMAND,
      (payload) => {
        setCanRedo(payload);
        return false;
      },
      1,
    );
  }, [editor]);

  React.useEffect(() => {
    const syncToolbarState = () => {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          setIsBold(false);
          setIsItalic(false);
          setActiveListType(null);
          return;
        }

        setIsBold(selection.hasFormat('bold'));
        setIsItalic(selection.hasFormat('italic'));

        const listType =
          selection.getNodes().map((node) => getListTypeFromNode(node as any)).find((value) => Boolean(value)) ??
          getListTypeFromNode(selection.anchor.getNode() as any);
        setActiveListType(listType ?? null);
      });
    };

    syncToolbarState();
    return editor.registerUpdateListener(() => {
      syncToolbarState();
    });
  }, [editor]);

  const closeLinkEditor = useCallback(() => {
    setLinkEditor(null);
  }, []);

  const openLinkEditor = useCallback((initialUrl = 'https://', nodeKey: string | null = null, rect?: DOMRect | null) => {
    const containerRect = toolbarContainerRef.current?.getBoundingClientRect() ?? null;
    const boundsRect = toolbarContainerRef.current
      ?.closest('[data-testid="tid.tools.shapeNotes.editor.surface"], [data-testid="tid.tools.shapeNotes.editor.modalSurface"]')
      ?.getBoundingClientRect() ?? null;
    setLinkEditor({
      url: initialUrl,
      originalUrl: initialUrl,
      nodeKey,
      isEditing: nodeKey === null,
      position: getLinkPanelPosition(rect, containerRect, boundsRect),
    });
  }, []);

  React.useEffect(() => {
    if (!linkEditor) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && linkEditorRef.current?.contains(target)) {
        return;
      }
      closeLinkEditor();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeLinkEditor();
      }
    };

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeLinkEditor, linkEditor]);

  React.useEffect(() => {
    return editor.registerCommand(
      CLICK_COMMAND,
      (event) => {
        if (disabled) return false;

        const target = event.target as HTMLElement | null;
        const anchor = target?.closest('a');
        const rootElement = editor.getRootElement();
        if (!anchor || !rootElement || !rootElement.contains(anchor)) {
          return false;
        }

        let nextNodeKey: string | null = null;
        let nextUrl = anchor.getAttribute('href') || 'https://';
        let isEditorLink = false;
        editor.read(() => {
          const lexicalNode = $getNearestNodeFromDOMNode(anchor);
          if (!lexicalNode) {
            return;
          }
          const linkNode = $isLinkNode(lexicalNode)
            ? lexicalNode
            : $findMatchingParent(lexicalNode, (parentNode) => $isLinkNode(parentNode));
          if ($isLinkNode(linkNode)) {
            isEditorLink = true;
            nextNodeKey = linkNode.getKey();
            nextUrl = linkNode.getURL();
          }
        });

        if (!isEditorLink) {
          return false;
        }

        event.preventDefault();
        openLinkEditor(nextUrl, nextNodeKey, anchor.getBoundingClientRect());
        return true;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [disabled, editor, openLinkEditor]);

  const handleLink = () => {
    if (disabled) return;

    let nextNodeKey: string | null = null;
    let nextUrl = 'https://';
    let canOpen = false;
    let selectionRect: DOMRect | null = null;

    editor.read(() => {
      const selection = $getSelection();
      const selectedLinkNode = getSelectedLinkNode();
      if (selectedLinkNode) {
        nextNodeKey = selectedLinkNode.getKey();
        nextUrl = selectedLinkNode.getURL();
        canOpen = true;
        return;
      }

      if ($isRangeSelection(selection) && selection.getTextContent().trim().length > 0) {
        canOpen = true;
        const domSelection = window.getSelection();
        if (domSelection && domSelection.rangeCount > 0) {
          selectionRect = domSelection.getRangeAt(0).getBoundingClientRect();
        }
      }
    });

    if (!canOpen) {
      toast.error(
        t('shapeNotes.toasts.linkSelectionRequired', {
          defaultValue: 'Select text or click an existing link',
        }),
      );
      return;
    }

    openLinkEditor(nextUrl, nextNodeKey, selectionRect);
  };

  const applyLink = useCallback(() => {
    const safeUrl = normalizeSafeHttpUrl(linkEditor?.url ?? '');
    if (!safeUrl) {
      toast.error(
        t('shapeNotes.toasts.enterValidHttpUrl', {
          defaultValue: 'Enter a valid http(s) URL',
        }),
      );
      return;
    }

    if (linkEditor?.nodeKey) {
      editor.update(() => {
        const node = $getNodeByKey(linkEditor.nodeKey!);
        if ($isLinkNode(node)) {
          node.setURL(safeUrl);
        }
      });
      setLinkEditor((current) => (
        current
          ? {
              ...current,
              url: safeUrl,
              originalUrl: safeUrl,
              isEditing: false,
            }
          : current
      ));
      return;
    }

    editor.focus(() => {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, safeUrl);
    });
    closeLinkEditor();
  }, [closeLinkEditor, editor, linkEditor, t]);

  const startEditingLink = useCallback(() => {
    setLinkEditor((current) => (
      current
        ? {
            ...current,
            isEditing: true,
          }
        : current
    ));
  }, []);

  const cancelEditingLink = useCallback(() => {
    setLinkEditor((current) => {
      if (!current) return current;
      if (!current.nodeKey) return null;
      return {
        ...current,
        url: current.originalUrl,
        isEditing: false,
      };
    });
  }, []);

  const removeLink = useCallback(() => {
    if (linkEditor?.nodeKey) {
      editor.update(() => {
        const node = $getNodeByKey(linkEditor.nodeKey!);
        if ($isLinkNode(node)) {
          unwrapLinkNode(node);
        }
      });
      closeLinkEditor();
      return;
    }

    editor.focus(() => {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    });
    closeLinkEditor();
  }, [closeLinkEditor, editor, linkEditor]);

  const handleBulletList = () => {
    if (disabled) return;
    editor.dispatchCommand(
      activeListType === 'bullet' ? REMOVE_LIST_COMMAND : INSERT_UNORDERED_LIST_COMMAND,
      undefined,
    );
  };

  const handleOrderedList = () => {
    if (disabled) return;
    editor.dispatchCommand(
      activeListType === 'number' ? REMOVE_LIST_COMMAND : INSERT_ORDERED_LIST_COMMAND,
      undefined,
    );
  };

  return (
    <div className="relative" ref={toolbarContainerRef}>
      <TooltipProvider delayDuration={150}>
        <div className="flex flex-wrap items-center gap-0.5 border-b bg-muted/30 px-1.5 py-1">
        <ToolbarTooltipButton
          label={t('shapeNotes.toolbar.undo', { defaultValue: 'Undo' })}
          className={BUTTON_CLASS}
          onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
          disabled={disabled || !canUndo}
          data-testid="tid.tools.shapeNotes.toolbar.undo"
        >
          <Undo2 className="h-3.5 w-3.5" />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          label={t('shapeNotes.toolbar.redo', { defaultValue: 'Redo' })}
          className={BUTTON_CLASS}
          onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
          disabled={disabled || !canRedo}
          data-testid="tid.tools.shapeNotes.toolbar.redo"
        >
          <Redo2 className="h-3.5 w-3.5" />
        </ToolbarTooltipButton>
        <div className="w-px h-4 bg-border mx-0.5" />
        <ToolbarTooltipButton
          label={t('shapeNotes.toolbar.bold', { defaultValue: 'Bold' })}
          className={getToolbarButtonClass(isBold)}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
          disabled={disabled}
          aria-pressed={isBold}
          data-testid="tid.tools.shapeNotes.toolbar.bold"
        >
          <Bold className="h-3.5 w-3.5" />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          label={t('shapeNotes.toolbar.italic', { defaultValue: 'Italic' })}
          className={getToolbarButtonClass(isItalic)}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
          disabled={disabled}
          aria-pressed={isItalic}
          data-testid="tid.tools.shapeNotes.toolbar.italic"
        >
          <Italic className="h-3.5 w-3.5" />
        </ToolbarTooltipButton>
        <div className="w-px h-4 bg-border mx-0.5" />
        <ToolbarTooltipButton
          label={t('shapeNotes.toolbar.bulletList', { defaultValue: 'Bullet list' })}
          className={getToolbarButtonClass(activeListType === 'bullet')}
          onClick={handleBulletList}
          disabled={disabled}
          aria-pressed={activeListType === 'bullet'}
          data-testid="tid.tools.shapeNotes.toolbar.bulletList"
        >
          <List className="h-3.5 w-3.5" />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          label={t('shapeNotes.toolbar.orderedList', { defaultValue: 'Ordered list' })}
          className={getToolbarButtonClass(activeListType === 'number')}
          onClick={handleOrderedList}
          disabled={disabled}
          aria-pressed={activeListType === 'number'}
          data-testid="tid.tools.shapeNotes.toolbar.orderedList"
        >
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          label={t('shapeNotes.toolbar.link', { defaultValue: 'Link' })}
          className={BUTTON_CLASS}
          onMouseDown={(event) => event.preventDefault()}
          onClick={handleLink}
          disabled={disabled}
          data-testid="tid.tools.shapeNotes.toolbar.link"
        >
          <Link2 className="h-4 w-4" />
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          label={t('shapeNotes.toolbar.uploadImage', { defaultValue: 'Upload image' })}
          className={BUTTON_CLASS}
          onClick={onUploadImage}
          disabled={disabled || isUploading}
          data-testid="tid.tools.shapeNotes.toolbar.uploadImage"
        >
          {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImageIcon className="h-3.5 w-3.5" />}
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          label={t('shapeNotes.toolbar.attachFile', { defaultValue: 'Attach file' })}
          className={BUTTON_CLASS}
          onClick={onUploadFile}
          disabled={disabled || isUploading}
          data-testid="tid.tools.shapeNotes.toolbar.attachFile"
        >
          {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
        </ToolbarTooltipButton>
        <ToolbarTooltipButton
          label={
            isExpanded
              ? t('shapeNotes.toolbar.collapseEditor', { defaultValue: 'Collapse editor' })
              : t('shapeNotes.toolbar.maximizeEditor', { defaultValue: 'Maximize editor' })
          }
          className={`${BUTTON_CLASS} ml-auto`}
          onClick={onToggleExpanded}
          data-testid="tid.tools.shapeNotes.toolbar.toggleExpanded"
        >
          {isExpanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
        </ToolbarTooltipButton>
        </div>
      </TooltipProvider>
      {linkEditor ? (
        <div ref={linkEditorRef}>
          <FloatingLinkEditorPanel
            state={linkEditor}
            onUrlChange={(nextUrl) => setLinkEditor((current) => (current ? { ...current, url: nextUrl } : current))}
            onApply={applyLink}
            onRemove={removeLink}
            onStartEditing={startEditingLink}
            onCancelEditing={cancelEditingLink}
          />
        </div>
      ) : null}
    </div>
  );
};

const ExternalStateSyncPlugin: React.FC<{ serializedState: string; hasPendingEdits: () => boolean }> = ({ serializedState, hasPendingEdits }) => {
  const [editor] = useLexicalComposerContext();
  const previousSerializedRef = useRef(serializedState);

  React.useEffect(() => {
    if (!serializedState || serializedState === previousSerializedRef.current) {
      return;
    }

    // A slow write (e.g. a server-backed org draft) echoes back an older
    // value. Replacing the editor then would drop text typed since.
    if (hasPendingEdits()) {
      return;
    }

    const currentState = JSON.stringify(editor.getEditorState().toJSON());
    if (currentState !== serializedState) {
      const nextState = editor.parseEditorState(serializedState);
      editor.setEditorState(nextState);
    }

    previousSerializedRef.current = serializedState;
  }, [editor, hasPendingEdits, serializedState]);

  return null;
};

const InsertAssetPlugin: React.FC<{
  nodeId: string;
  isUploading: boolean;
  onUploadingChange: (nextValue: boolean) => void;
  onSerializedChange: (nextSerialized: string) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
}> = ({ nodeId, isUploading, onUploadingChange, onSerializedChange, isExpanded, onToggleExpanded }) => {
  const [editor] = useLexicalComposerContext();
  const { t } = useTranslation('diagram');
  const authenticatedFetch = useAuthenticatedFetch();
  const { diagramId } = useDiagramDocument();
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadMutation = useMutation({
    mutationFn: async (file: File) => uploadAsset(authenticatedFetch, diagramId, nodeId, file),
  });

  React.useEffect(() => {
    onUploadingChange(uploadMutation.isPending);
  }, [onUploadingChange, uploadMutation.isPending]);

  const insertImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('shapeNotes.toasts.selectImageFile', { defaultValue: 'Select an image file' }));
      return;
    }

    try {
      const asset = await uploadMutation.mutateAsync(file);
      editor.update(() => {
        insertAssetNodes([
          $createAssetImageNode(asset.id, asset.fileName),
          $createParagraphNode(),
        ]);
      });
      onSerializedChange(JSON.stringify(editor.getEditorState().toJSON()));
      toast.success(
        t('shapeNotes.toasts.imageUploaded', {
          fileName: asset.fileName,
          defaultValue: '{{fileName}} uploaded',
        }),
      );
    } catch (error) {
      toast.error(
        uploadErrorMessage(
          error,
          t('shapeNotes.toasts.uploadImageFailed', { defaultValue: 'Failed to upload image' }),
        ),
      );
    }
  };

  const insertAttachment = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    try {
      const asset = await uploadMutation.mutateAsync(file);
      editor.update(() => {
        insertAssetNodes([
          $createAssetAttachmentNode(asset.id, asset.fileName, asset.mimeType, asset.sizeBytes),
          $createParagraphNode(),
        ]);
      });
      onSerializedChange(JSON.stringify(editor.getEditorState().toJSON()));
      toast.success(
        t('shapeNotes.toasts.fileAttached', {
          fileName: asset.fileName,
          defaultValue: '{{fileName}} attached',
        }),
      );
    } catch (error) {
      toast.error(
        uploadErrorMessage(
          error,
          t('shapeNotes.toasts.attachFileFailed', { defaultValue: 'Failed to attach file' }),
        ),
      );
    }
  };

  return (
    <>
      <ToolbarPlugin
        disabled={false}
        onUploadImage={() => imageInputRef.current?.click()}
        onUploadFile={() => fileInputRef.current?.click()}
        isUploading={isUploading}
        isExpanded={isExpanded}
        onToggleExpanded={onToggleExpanded}
      />
      <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={insertImage} data-testid="tid.tools.shapeNotes.input.uploadImage" />
      <input ref={fileInputRef} type="file" className="hidden" onChange={insertAttachment} data-testid="tid.tools.shapeNotes.input.attachFile" />
    </>
  );
};

const ReadOnlyToolbarPlugin: React.FC<{ isExpanded: boolean; onToggleExpanded: () => void }> = ({ isExpanded, onToggleExpanded }) => {
  const { t } = useTranslation('diagram');
  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2 text-xs text-muted-foreground" data-testid="tid.tools.shapeNotes.readOnly">
        <span>{t('shapeNotes.readOnly', { defaultValue: 'Notes are read-only' })}</span>
        <ToolbarTooltipButton
          label={
            isExpanded
              ? t('shapeNotes.toolbar.collapseEditor', { defaultValue: 'Collapse editor' })
              : t('shapeNotes.toolbar.maximizeEditor', { defaultValue: 'Maximize editor' })
          }
          className={`${BUTTON_CLASS} ml-auto`}
          onClick={onToggleExpanded}
          data-testid="tid.tools.shapeNotes.toolbar.toggleExpanded"
        >
          {isExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
        </ToolbarTooltipButton>
      </div>
    </TooltipProvider>
  );
};

// One editor instance per node, so a commit on unmount reaches the node it was typed for.
export const ShapeNotesEditor: React.FC<ShapeNotesEditorProps> = (props) => (
  <ShapeNotesNodeEditor key={props.nodeId} {...props} />
);

const ShapeNotesNodeEditor: React.FC<ShapeNotesEditorProps> = ({ nodeId, value, onChange, disabled = false }) => {
  const { t } = useTranslation('diagram');
  const { serialized } = useMemo(() => normalizeLexicalValue(value), [value]);
  const [isUploading, setIsUploading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const pendingSerializedRef = useRef<string | null>(null);
  const lastCommittedSerializedRef = useRef(serialized);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Each commit can be a server round trip and a new draft change set, so
  // typing only stages the value; it is committed on Done, blur, or unmount.
  const flushPendingCommit = useCallback(() => {
    const pendingSerialized = pendingSerializedRef.current;
    if (!pendingSerialized || pendingSerialized === lastCommittedSerializedRef.current) {
      pendingSerializedRef.current = null;
      return;
    }

    try {
      onChangeRef.current(JSON.parse(pendingSerialized) as Record<string, unknown>);
      lastCommittedSerializedRef.current = pendingSerialized;
      pendingSerializedRef.current = null;
    } catch {
      pendingSerializedRef.current = null;
    }
  }, []);

  const commitNow = useCallback((nextSerialized: string) => {
    pendingSerializedRef.current = nextSerialized;
    flushPendingCommit();
  }, [flushPendingCommit]);

  const hasPendingEdits = useCallback(() => pendingSerializedRef.current !== null, []);

  const handleSurfaceBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    const nextFocus = event.relatedTarget;
    if (nextFocus instanceof Node && event.currentTarget.contains(nextFocus)) {
      return;
    }
    flushPendingCommit();
  }, [flushPendingCommit]);

  useEffect(() => {
    lastCommittedSerializedRef.current = serialized;
  }, [serialized]);

  useEffect(() => {
    // Capture phase runs before the canvas changes selection on the same press,
    // which would otherwise retarget or reject the commit.
    const handlePointerDown = (event: PointerEvent) => {
      if (pendingSerializedRef.current === null) return;
      if (event.target instanceof Node && surfaceRef.current?.contains(event.target)) return;
      flushPendingCommit();
    };
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (pendingSerializedRef.current === null) return;
      flushPendingCommit();
      event.preventDefault();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      flushPendingCommit();
    };
  }, [flushPendingCommit]);

  const handleExpandedChange = useCallback((nextValue: boolean) => {
    flushPendingCommit();
    setIsExpanded(nextValue);
  }, [flushPendingCommit]);

  const initialConfig = useMemo(() => ({
    namespace: `shape-notes-${nodeId}`,
    editable: !disabled,
    editorState: serialized,
    onError: (error: Error) => {
      throw error;
    },
    theme: {
      paragraph: 'mb-2',
      heading: {
        h1: 'text-xl font-bold mb-2 mt-3',
        h2: 'text-lg font-bold mb-2 mt-2.5',
        h3: 'text-base font-semibold mb-1.5 mt-2',
      },
      quote: 'border-l-2 border-border pl-3 text-muted-foreground italic',
      list: {
        ul: 'list-disc pl-5 my-2',
        ol: 'list-decimal pl-5 my-2',
        listitem: 'mb-1',
      },
      link: 'cursor-pointer text-primary underline underline-offset-2',
      text: {
        bold: 'font-semibold',
        italic: 'italic',
      },
    },
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, AssetImageNode, AssetAttachmentNode],
  }), [disabled, nodeId, serialized]);

  const renderEditorSurface = (expanded: boolean) => (
    <div className="overflow-hidden rounded-xl border bg-background shadow-sm" data-testid={expanded ? 'tid.tools.shapeNotes.editor.modalSurface' : 'tid.tools.shapeNotes.editor.surface'} ref={surfaceRef} onBlur={handleSurfaceBlur}>
      <LexicalComposer initialConfig={initialConfig}>
        {disabled ? (
          <ReadOnlyToolbarPlugin isExpanded={expanded} onToggleExpanded={() => handleExpandedChange(!expanded)} />
        ) : (
          <InsertAssetPlugin
            nodeId={nodeId}
            isUploading={isUploading}
            onUploadingChange={setIsUploading}
            onSerializedChange={commitNow}
            isExpanded={expanded}
            onToggleExpanded={() => handleExpandedChange(!expanded)}
          />
        )}
        <div className={disabled ? 'relative cursor-default' : 'relative cursor-text'}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                data-testid={expanded ? 'tid.tools.shapeNotes.editor.input.modal' : 'tid.tools.shapeNotes.editor.input'}
                className={
                  expanded
                    ? `max-h-[60vh] min-h-[60vh] overflow-y-auto px-4 py-3 text-sm leading-6 outline-none ${disabled ? 'cursor-default' : 'cursor-text'}`
                    : `min-h-[220px] px-4 py-3 text-sm leading-6 outline-none ${disabled ? 'cursor-default' : 'cursor-text'}`
                }
              />
            }
            placeholder={<Placeholder />}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <MarkdownShortcutPlugin transformers={NOTES_MARKDOWN_TRANSFORMERS} />
          <ClickableLinkPlugin newTab disabled={!disabled} />
          <OnChangePlugin
            ignoreSelectionChange
            onChange={(editorState) => {
              const nextSerialized = JSON.stringify(editorState.toJSON());
              // Lexical may emit a normalized initial state when an empty
              // editor is mounted for a newly selected shape. That state can
              // differ structurally from the stored empty value even though
              // the user made no edit. Never turn that initialization into a
              // shape-field command or scenario draft.
              if (!projectRichTextToPlainText(serialized).trim()
                && !projectRichTextToPlainText(nextSerialized).trim()) {
                pendingSerializedRef.current = null;
                lastCommittedSerializedRef.current = nextSerialized;
                return;
              }
              if (nextSerialized === lastCommittedSerializedRef.current) {
                pendingSerializedRef.current = null;
                return;
              }
              pendingSerializedRef.current = nextSerialized;
            }}
          />
          <ExternalStateSyncPlugin serializedState={serialized} hasPendingEdits={hasPendingEdits} />
        </div>
      </LexicalComposer>
      {isUploading ? (
        <div className="border-t bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground" data-testid="tid.tools.shapeNotes.uploading">
          {t('shapeNotes.uploading', { defaultValue: 'Uploading...' })}
        </div>
      ) : null}
    </div>
  );

  return (
    <>
      {!isExpanded ? renderEditorSurface(false) : null}
      <Dialog open={isExpanded} onOpenChange={handleExpandedChange}>
        <DialogContent className="w-[min(960px,calc(100vw-2rem))] max-w-[960px] gap-0 overflow-hidden border-0 p-0 shadow-xl" showCloseButton={false} data-testid="tid.tools.shapeNotes.modal">
          <DialogTitle className="sr-only">
            {t('shapeNotes.dialogTitle', { defaultValue: 'Shape notes' })}
          </DialogTitle>
          {renderEditorSurface(true)}
        </DialogContent>
      </Dialog>
    </>
  );
};
